import type { CategoryPreferences, Destination } from "@segment/consent-manager/types/types";
import * as request from "superagent";
import { HttpMethods, buildSignatureAuthHeaders } from "@services/requests";
import { apiBaseURL } from "@utils/apiHelpers";

const GEO_LOCATION = `${apiBaseURL("v2")}/user/geolocation/`;

/**
 * Fetch region information using backend API
 * /api/v2/user/geolocation/
 */
const getRegionConsent = async () => {
  try {
    const { authorization_header, http_signature_created } = buildSignatureAuthHeaders(
      HttpMethods.GET,
      GEO_LOCATION
    );

    const response = await request
      .get(GEO_LOCATION)
      .set("Authorization", authorization_header)
      .set("Created", http_signature_created);

    const data = response.body?.data;
    return data?.country_code === "CA" && data?.region_code === "QC" ? false : true;
  } catch (error) {
    return true;
  }
};

const NON_ESSENTIAL_INTEGRATIONS = [
  "Facebook Pixel",
  "FullStory",
  "Google Analytics",
  "Google Tag Manager",
  "Iterable",
  "Kustomer",
  "MovableInk",
  "Pinterest Tag",
  "Impact Partnership Cloud",
  "Visual Website Optimizer",
  "Webhooks",
];

/**
 * Reducer function to take all integration strings and set them equal to true
 * @param preferences Accumulator object for initial preferences
 * @param integration String name of the current integration being processed
 * @returns An object with all integrations as keys with their corresponding values set to True
 */
const initialPreferenceReducer = (preferences, integration, consent) => {
  return {
    ...preferences,
    [integration]: consent,
  };
};

export const segmentCustomCategories = {
  "Non-Essential": {
    purpose: "A consent category for all non-essential tracking",
    integrations: [...NON_ESSENTIAL_INTEGRATIONS],
  },
};

// Initialize empty preferences
export let segmentInitialPreferences = {};

/**
 * Set initial preferences dynamically based on region
 */
const initialPreferences = async () => {
  const consent = await getRegionConsent();
  Object.keys(segmentCustomCategories).forEach((integration) => {
    segmentInitialPreferences[integration] = consent;
  });
};

// Call initialPreferences to dynamically set initial preferences
initialPreferences();

/**
 * Function expected by ConsentManagerBuilder to map destination preferences to custom categories
 * @param destinations An array of destinations handled by the consent manager
 * @param preferences An object with keys equal to either a custom category name or an uncategorized destination name,
 *                    with corresponding boolean values for consent
 * @returns An object with the filled out `destinationPreferences` and `customPreferences`
 */
export const segmentMapCustomPreferences = (
  destinations: Destination[],
  preferences: CategoryPreferences
) => {
  const destinationPreferences = {};
  const customPreferences = {};

  for (const preferenceName of Object.keys(segmentCustomCategories)) {
    const value = preferences[preferenceName];
    if (typeof value === "boolean" || typeof value === "string") {
      customPreferences[preferenceName] = value;
    } else {
      customPreferences[preferenceName] = true;
    }
  }

  destinations.forEach((destination) => {
    // Set uncategorized destinations, or undefined for category destinations
    destinationPreferences[destination.id] = preferences[destination.id];
    // Find and set value for category destinations
    if (destinationPreferences[destination.id] === undefined) {
      Object.entries(segmentCustomCategories).forEach(([categoryName, { integrations }]) => {
        const isCategoryIntegration = integrations.includes(destination.id);
        if (isCategoryIntegration) {
          destinationPreferences[destination.id] = customPreferences[categoryName];
        }
      });
    }
  });

  return { destinationPreferences, customPreferences };
};
